import { Authenticated, Refine } from '@refinedev/core'
import routerProvider from '@refinedev/react-router-v6'
import { Navigate, Route, Routes } from 'react-router-dom'

import { AppLayout } from '@/layout/layout'
import { authProvider } from '@/providers/auth-provider'
import { dataProvider } from '@/providers/data-provider'
import LoginPage from '@/pages/login'
import ProjectListPage from '@/pages/projects/list'
import ProjectCreatePage from '@/pages/projects/create'
import ProjectShowPage from '@/pages/projects/show'
import DashboardPage from '@/pages/dashboard'
import SettingsPage from '@/pages/settings'

/**
 * App：Refine 主应用
 *
 * 路由结构：
 *   /login              登录页（无需鉴权）
 *   /                   → 重定向到 /projects（受 Authenticated 守卫保护）
 *   /projects           项目货架（首页）
 *   /projects/create    新建项目
 *   /projects/:code     项目详情（含 5 个 Tab）
 *   /dashboard          跨项目统计看板
 *   /settings           设置页（用户信息 + 修改密码 + 基础设置）
 *
 * 认证守卫：用 <Authenticated> 包裹 <AppLayout />，
 * 未登录时 fallback 重定向到 /login，避免直接进入 /projects 后白屏。
 */

export default function App() {
  return (
    <Refine
      routerProvider={routerProvider}
      dataProvider={dataProvider}
      authProvider={authProvider}
      // 用 Antd 提供的 ThemedLayout 默认主题（实际用我们自定义的 ThemeProvider 覆盖）
      resources={[
        { name: 'projects', list: '/projects', create: '/projects/create', show: '/projects/:code' },
        { name: 'project-licenses' },
        { name: 'project-devices' },
        { name: 'project-activations' },
        { name: 'project-stats' },
        { name: 'platform-stats' }
      ]}
      options={{
        syncWithLocation: true,
        warnWhenUnsavedChanges: true
      }}
    >
      <Routes>
        {/* 登录页：无需鉴权，已登录时重定向到 /projects */}
        <Route
          path="/login"
          element={
            <Authenticated key="login" fallback={<LoginPage />}>
              <Navigate to="/projects" replace />
            </Authenticated>
          }
        />

        {/* 受保护路由：未登录时重定向到 /login */}
        <Route
          path="/"
          element={
            <Authenticated key="app" fallback={<Navigate to="/login" replace />}>
              <AppLayout />
            </Authenticated>
          }
        >
          <Route index element={<Navigate to="/projects" replace />} />
          <Route path="projects" element={<ProjectListPage />} />
          <Route path="projects/create" element={<ProjectCreatePage />} />
          <Route path="projects/:code" element={<ProjectShowPage />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="settings" element={<SettingsPage />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Refine>
  )
}
